// Interface for color
import { PaginatedApi } from "@/data/pagination";

export interface VariantColor {
  id: string; // Color ID
  name: string; // Color name
  hex: string; // Color hex code
}

// Interface for Variant
export interface Variant {
  ean: string; // Product EAN code
  color?: VariantColor; // Product color
  catalogImgUrl: string; // Product image URL
  defaultVariant: boolean; // Indicates if the variant is the default
  targetPrice: number; // Product target price
  available: boolean; // Indicates if the variant is available
  size: string; // Variant size
}

// Interface for Product
export interface Product {
  id: string; // Product ID
  title: string; // Product title
  brand: string; // Product brand
  minVariantTargetPrice: number; // Product lowest price
  variants: Variant[]; // Array of product variants
}

// Type for the search endpoint response
export type SearchProductsResponse = PaginatedApi<Product>;

// Optional error responses
export interface ErrorResponse {
  message: string; // Description of the error
  statusCode: number; // HTTP status code
}

export interface SearchProductsPriceFilters {
  maxPrice?: number | null;
  minPrice?: number | null;
}

export interface SearchProductsSortFilters {
  sortByAsc?: "minVariantTargetPrice" | null;
  sortByDesc?: "minVariantTargetPrice" | null;
}

export interface SearchProductsBrandFilters {
  brand?: string[];
}

export interface SearchProductsColorFilters {
  colorId?: string[];
}

export interface SearchProductsFilters
  extends SearchProductsPriceFilters,
    SearchProductsSortFilters,
    SearchProductsBrandFilters,
    SearchProductsColorFilters {}

export interface SearchProductsQueryParams extends SearchProductsFilters {
  term?: string;
  categoryId?: string;
  pageNumber?: number;
  pageSize?: number;
  price?: string;
  gender?: string[];
  size?: string[];
  colorId?: string[];
}

export type GenderItem = {
  it: string;
  en: string;
};

export type GenderKey = keyof typeof genderBase;

export type Gender = {
  woman: GenderItem;
  "not-applicable": GenderItem;
  unisex: GenderItem;
  man: GenderItem;
  child: GenderItem;
};

export const genderBase: Gender = {
  woman: { it: "Donna", en: "Woman" },
  "not-applicable": { it: "Non applicabile", en: "Not applicable" },
  unisex: { it: "Unisex", en: "Unisex" },
  man: { it: "Uomo", en: "Man" },
  child: { it: "Bambino", en: "Child" },
};

export function getGenderFilter(lang: keyof GenderItem, gender: keyof Gender) {
  return genderBase[gender][lang];
}

export interface CatalogProductViewResponse {
  catalogProductViewsList: Product[];
}

export interface CatalogProductView {
  productCode: string;
  title: string;
  brand: string;
  categoryId: string;
  productId: CatalogProductViewProductId;
  variants: CatalogProductViewVariant[];
}

export interface CatalogProductViewProductId {
  timestamp: number;
  counter: number;
  randomValue1: number;
  randomValue2: number;
  date: string; // ISO date format: "2022-03-10"
}

export interface CatalogProductViewVariant {
  ean: string;
  imgUrl: string[];
  size: string;
  colorId: string;
  catalogImgUrl: string;
  defaultVariant: boolean;
  avgOnlinePrice: number;
  author: string;
  lastAvgPriceUpdateDate: string; // ISO datetime format: "2022-03-10T16:15:50Z"
  creationDate: string;
  lastUpdateDate: string;
  keepaEnriched: boolean;
  asin: string;
}
